import { useEffect, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { Icon } from '../../components/ui/Icon';

/**
 * Popover "Calendario": chiede a POST /tasks/calendar-token l'URL del feed ICS
 * personale e lo mostra con tasto Copia + istruzione per Google Calendar.
 * Il token si genera solo all'apertura (mai al mount), una volta per sessione;
 * "Rigenera" ne fa uno nuovo (il vecchio link smette di funzionare).
 */
export function CalendarFeedPopover({ compact, iconOnly }: { compact?: boolean; iconOnly?: boolean }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  // L'API risponde { token, feedUrl } (il contratto diceva { url }): si accettano entrambi
  const fetchToken = (regenerate = false) => {
    setLoading(true);
    api.post<{ feedUrl?: string; url?: string }>('/tasks/calendar-token', { regenerate })
      .then((r) => { const u = r.feedUrl ?? r.url ?? null; if (!u) throw new Error('Feed non disponibile'); setUrl(u); setError(null); })
      .catch((e: any) => setError(e?.message ?? 'Feed non disponibile'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!open || url || loading) return;
    fetchToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, url, loading]);

  const regenerate = async () => {
    const ok = await confirmDialog({
      title: 'Rigenerare il link del calendario?',
      body: 'Il vecchio link smette di funzionare: andrà sostituito anche in Google Calendar.',
      confirmLabel: 'Rigenera',
      icon: 'refresh',
    });
    if (ok) fetchToken(true);
  };

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onClick);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onClick); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const copy = () => {
    if (!url) return;
    navigator.clipboard.writeText(url)
      .then(() => toast.ok('Link copiato', 'Incollalo in Google Calendar, Altri calendari > Da URL.'))
      .catch(() => toast.alert('Copia non riuscita', 'Seleziona il link e copialo a mano.'));
  };

  return (
    <div ref={ref} className="relative">
      {iconOnly ? (
        // Comando raro: un'icona sola nella barra della pagina (Task, 26/10/2026).
        <button
          className="btn btn-sm btn-ghost btn-icon"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label="Vedi i task nel tuo Google Calendar"
          title="Vedi i task nel tuo Google Calendar"
        >
          <Icon name="calendarPlus" size={14} />
        </button>
      ) : (
        <button
          className={compact ? 'btn btn-sm' : 'btn'}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="dialog"
          aria-expanded={open}
          title="Feed calendario personale (ICS)"
        >
          <Icon name="calendar" size={12} /> Calendario
        </button>
      )}
      {open && (
        <div className="dropdown right-0 mt-1" style={{ width: 360, padding: 14 }} role="dialog" aria-label="Feed calendario">
          <div className="t-micro mb-2">Il tuo calendario dei task</div>
          <div className="text-[13px] mute mb-3">
            Un link che Google Calendar legge da solo: scadenze e promemoria dei tuoi task compaiono lì, aggiornati ogni qualche ora.
          </div>
          {loading && <div className="text-[13px] mute">Preparo il link…</div>}
          {error && <div className="text-[13px] text-alert">{error}</div>}
          {url && (
            <>
              <div className="flex gap-2">
                <input className="inp mono text-[12px]" readOnly value={url} onFocus={(e) => e.currentTarget.select()} aria-label="URL del feed" />
                <button className="btn btn-icon" onClick={copy} title="Copia il link" aria-label="Copia il link"><Icon name="copy" size={14} /></button>
              </div>
              <div className="text-[12px] mute mt-2.5">
                In Google Calendar: <strong>Altri calendari</strong> &gt; <strong>Da URL</strong>, incolla il link.
              </div>
              <div className="flex justify-end mt-2">
                <button className="btn btn-sm btn-ghost" disabled={loading} onClick={regenerate} title="Nuovo link, il vecchio smette di funzionare">
                  <Icon name="refresh" size={11} /> Rigenera
                </button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
